import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { initCartFrontrowQuant } from './cart-frontrow-quant.js';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { cartItemIsPremiumSubscription } from './subscription-tier.js';

function cartItemIsDuoSubscription(item) {
  const plan = String(item?.selling_plan_allocation?.selling_plan?.name || '');
  const blob = `${item?.sku || ''} ${item?.variant_title || item?.title || ''} ${plan}`.toUpperCase();
  if (/TRIPLE\s*PACK|3\s*[-]?\s*PACK|\b3PK\b|12\s*[-]?\s*WEEK|3\s*[-]?\s*MONTH/.test(blob)) return false;
  return /DOUBLE\s*PACK|2\s*[-]?\s*PACK|\b2PK\b|\bDUO\b|8\s*[-]?\s*WEEK|2\s*[-]?\s*MONTH/.test(blob);
}

/** @typedef {import('./utilities').TextComponent} TextComponent */

/** All gift variant IDs – knot bags, tumbler, wave straws. */
const GIFT_VARIANT_IDS = [54353647731001, 54353646420281, 54353638392121, 54353644880185, 54353640784185, 67583119098169, 67583126470969, 67583131124025, 67583133385017, 67583115690297, 67955807748409, 67955813712185];

/** Gifts included with 12-week subscriptions only (tumbler). */
const GIFT_TUMBLER_VARIANT_IDS = [54353644880185, 67583115690297];
/** Version B 3-month extra canister gifts. */
const GIFT_EXTRA_CANISTER_VARIANT_IDS = [67955807748409, 67955813712185];
/** Gifts included with 8-week and 12-week subscriptions (wave straw). */
const GIFT_STRAW_VARIANT_IDS = [54353638392121, 54353640784185, 67583131124025, 67583133385017];

/**
 * @param {{ variant_id: number; selling_plan_allocation?: { selling_plan: { name: string } } }} item
 * @returns {boolean}
 */
function cartItemIsPaidSubscription(item) {
  return Boolean(item.selling_plan_allocation) && !GIFT_VARIANT_IDS.includes(item.variant_id);
}

/**
 * @param {HTMLTableRowElement[]} rows
 * @param {number} line - Shopify 1-based cart line index.
 * @returns {HTMLTableRowElement | undefined}
 */
function getCartRowByLine(rows, line) {
  const lineKey = String(line);
  return rows.find((row) => row.dataset.cartLine === lineKey);
}

/**
 * Removes legacy "Saving X%" subtotal markup (badge or orphan text nodes).
 * @param {ParentNode} root
 */
function removeStaleCartSavingsLabels(root) {
  root.querySelectorAll('.cart__savings-badge').forEach((el) => el.remove());
  root.querySelectorAll('.cart__subtotal-pricing').forEach((pricing) => {
    for (const node of [...pricing.childNodes]) {
      if (node.nodeType === Node.TEXT_NODE && /^\s*Saving\s*%?\s*$/i.test(node.textContent ?? '')) {
        node.remove();
      }
    }
  });
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();
    removeStaleCartSavingsLabels(this);
    initCartFrontrowQuant(this);

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = getCartRowByLine(this.refs.cartItemRows, line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * Gift rules:
   * - Last subscription removed → remove all gifts.
   * - 12-week subscription removed but another subscription remains → remove tumbler + straw only.
   * - 4-week subscription removed while 12-week remains → gifts stay.
   * - No paid subscriptions left → remove any orphan gifts.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = getCartRowByLine(this.refs.cartItemRows, line);
    if (!cartItemRowToRemove?.dataset.key) return;

    if (cartItemRowToRemove.dataset.isGift === 'true') {
      return;
    }

    const isPaidSubscription =
      cartItemRowToRemove.dataset.hasSellingPlan === 'true' &&
      cartItemRowToRemove.dataset.isGift !== 'true';

    if (isPaidSubscription) {
      const subscriptionRows = this.refs.cartItemRows.filter(
        (row) => row.dataset.hasSellingPlan === 'true' && row.dataset.isGift !== 'true'
      );
      const remainingRows = subscriptionRows.filter((row) => row !== cartItemRowToRemove);
      /** @type {Record<string, number>} */
      const updates = { [cartItemRowToRemove.dataset.key]: 0 };

      if (remainingRows.length === 0) {
        this.refs.cartItemRows.forEach((row) => {
          const vid = Number(row.dataset.variantId);
          if (GIFT_VARIANT_IDS.includes(vid) && row.dataset.key) {
            updates[row.dataset.key] = 0;
          }
        });
      } else {
        const remainingTiers = remainingRows.map((row) => row.dataset.subscriptionTier);
        const remainingHasPremiumTier = remainingTiers.includes('premium');
        const remainingHasDuoTier = remainingTiers.includes('duo');
        if (!remainingHasPremiumTier) {
          this.refs.cartItemRows.forEach((row) => {
            const vid = Number(row.dataset.variantId);
            if (!row.dataset.key) return;
            if (GIFT_TUMBLER_VARIANT_IDS.includes(vid) || GIFT_EXTRA_CANISTER_VARIANT_IDS.includes(vid)) {
              updates[row.dataset.key] = 0;
            }
            if (!remainingHasDuoTier && GIFT_STRAW_VARIANT_IDS.includes(vid)) {
              updates[row.dataset.key] = 0;
            }
          });
        }
      }

      this.#applyCartLineUpdates(updates);
      return;
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });

    this.#syncGiftsWithSubscriptions();
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        removeStaleCartSavingsLabels(this);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Applies cart/update.js line changes (quantity 0 removes the line).
   * @param {Record<string, number>} updates - Map of line item keys to quantities.
   * @param {{ runGiftSync?: boolean }} [options]
   */
  #applyCartLineUpdates(updates, { runGiftSync = true } = {}) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');

    this.#disableCartItems();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#enableCartItems();
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections?.[this.sectionId] || '',
          'text/html'
        );
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        if (parsedResponseText.items) {
          this.#updateQuantitySelectors(parsedResponseText);
        }

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        if (parsedResponseText.sections?.[this.sectionId]) {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        } else {
          await sectionRenderer.renderSection(this.sectionId, { cache: false });
        }
        removeStaleCartSavingsLabels(this);

        this.#updateCartQuantitySelectorButtonStates();
        if (runGiftSync) {
          await this.#syncGiftsWithSubscriptions();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Removes orphan or tier-mismatched gifts based on cart.js subscription lines.
   */
  #syncGiftsWithSubscriptions() {
    const cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return fetch(`${cartRoot}cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        const paidSubscriptions = (cart.items || []).filter(cartItemIsPaidSubscription);
        /** @type {Record<string, number>} */
        const updates = {};

        if (paidSubscriptions.length === 0) {
          for (const item of cart.items || []) {
            if (GIFT_VARIANT_IDS.includes(item.variant_id) && item.key) {
              updates[item.key] = 0;
            }
          }
        } else {
          const hasPremiumTier = paidSubscriptions.some(cartItemIsPremiumSubscription);
          const hasDuoTier = paidSubscriptions.some(cartItemIsDuoSubscription);
          if (!hasPremiumTier) {
            for (const item of cart.items || []) {
              if (!item.key) continue;
              if (GIFT_TUMBLER_VARIANT_IDS.includes(item.variant_id) || GIFT_EXTRA_CANISTER_VARIANT_IDS.includes(item.variant_id)) {
                updates[item.key] = 0;
              }
              if (!hasDuoTier && GIFT_STRAW_VARIANT_IDS.includes(item.variant_id)) {
                updates[item.key] = 0;
              }
            }
          }
        }

        if (Object.keys(updates).length === 0) {
          return;
        }

        return this.#applyCartLineUpdates(updates, { runGiftSync: false });
      })
      .catch((error) => {
        console.error('Gift sync failed:', error);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      removeStaleCartSavingsLabels(this);
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail?.data?.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      removeStaleCartSavingsLabels(this);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      removeStaleCartSavingsLabels(this);
    }

    initCartFrontrowQuant(this);
    this.#syncGiftsWithSubscriptions();
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
